import React, { useState, useEffect, useRef } from "react";
import "./TeachersList.css";
import { te } from "date-fns/locale";
import { Slice } from "lucide-react";

export const DEFAULT_IMAGE = "/nopfp.jpg";
const BASE_URL = "https://preview.zushm.cz";

interface Teacher {
  name: string;
  roles: string[]
  subjects: string[];
  email: string;
  zivotopis: string | null;
  images: string[];
  profilePicture: string;
}

interface ApiTeacher {
  Jmeno: string;
  Obor: string;
  Vyucuje: string;
  Image: string | null;
  Zivotopis: string | null;
  image?: string | string[];
}

const normalizeEmail = (email: string) => {
  return email
    .toLowerCase()
    .replace(/[áàãâä]/g, "a")
    .replace(/[čć]/g, "c")
    .replace(/[ď]/g, "d")
    .replace(/[éèêë]/g, "e")
    .replace(/[íìîï]/g, "i")
    .replace(/[ľ]/g, "l")
    .replace(/[ň]/g, "n")
    .replace(/[óòôö]/g, "o")
    .replace(/[ř]/g, "r")
    .replace(/[š]/g, "s")
    .replace(/[ť]/g, "t")
    .replace(/[úùûüů]/g, "u")
    .replace(/[ýÿ]/g, "y")
    .replace(/[ž]/g, "z");
};

const normalizeTeacherImages = (imageData: string[] | string | null | undefined): string[] => {
  if (!imageData) return [DEFAULT_IMAGE];

  let imagesArray: string[] = [];

  if (Array.isArray(imageData)) {
    // Combine every two consecutive elements
    for (let i = 0; i < imageData.length; i += 2) {
      const part1 = imageData[i]?.trim() || "";
      const part2 = imageData[i + 1]?.trim() || "";
      if (part1 && part2) {
        imagesArray.push(part1 + part2);
      }
    }
  } else if (typeof imageData === "string") {
    // Split comma-separated string if necessary
    imagesArray = imageData.split(",").map((i) => i.trim()).filter(Boolean);
  }

  return imagesArray.length > 0 ? imagesArray : [DEFAULT_IMAGE];
};

const TeachersList: React.FC = () => {
  const [filter, setFilter] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await fetch("https://preview.zushm.cz/api/zamestanci/");
        const rawData: ApiTeacher[] = await response.json();

        const processedTeachers = rawData.map((teacher: ApiTeacher) => {
          // Extract name
          const nameParts = teacher.Jmeno.split(", ");
          let name = nameParts[0];
          const nameSplit = name.split(" ");

          // Generate email
          let email = "";
          if (nameSplit.length > 1) {
            const [firstName, lastName] = nameSplit;
            email = `${firstName.charAt(0).toLowerCase()}.${lastName.toLowerCase()}@zushm.cz`;
          }

          name = teacher.Jmeno

          // Special case for director
          if (teacher.Jmeno === "Štěpán Křováček") {
            email = "reditel@zushm.cz";
          }

          // Determine role
          let roles: string[] = [];
          switch (teacher.Obor.toLowerCase()) {
          case "hudebni":
            roles.push("hudebního oboru");
            break;
          case "tanecni":
            roles.push("tanečního oboru");
            break;
          case "vytvarny":
            roles.push("výtvarného oboru");
            break;
          case "administrativa":
            roles.push("Administrativní pracovník");
            break;
          }
          
          const forcedAdministration = [
            "Štěpán Křováček",
            "Lukáš Kyncl, Dis.",
            ""
          ];

          if (forcedAdministration.includes(teacher.Jmeno)) {
            roles.push("Administrativní pracovník");
          }

          const allImages = normalizeTeacherImages(teacher.Image || teacher.image);

          let images = allImages;
          let profilePicture = allImages[0];

          if (allImages.length > 1) {
            images = allImages.slice(1);
            profilePicture = allImages[0];
          }

          return {
            name,
            roles,
            subjects: teacher.Vyucuje ? teacher.Vyucuje.split(", ").filter(s => s.trim()) : [],
            email: email ? normalizeEmail(email) : "",
            zivotopis: teacher.Zivotopis || null,
            images: images,
            profilePicture,
          };
        });

        setTeachers(processedTeachers);
      } catch (error) {
        console.error("Error fetching teachers:", error);
      }
    };

    fetchTeachers();
  }, []);

  useEffect(() => {
    if (selectedTeacher && selectedTeacher.images.length > 1) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === selectedTeacher.images.length - 1 ? 0 : prev + 1
        );
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [selectedTeacher]);

  const handleTeacherClick = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedTeacher) {
      resetInterval();
      setCurrentImageIndex((prev) => 
        prev === selectedTeacher.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (selectedTeacher) {
      // Reset interval when manually navigating
      resetInterval();
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedTeacher.images.length - 1 : prev - 1
      );
    }
  };

  const handleThumbnailClick = (index: number) => {
    if (selectedTeacher) {
      // Reset interval when manually navigating
      resetInterval();
      setCurrentImageIndex(index);
    }
  };

  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    if (selectedTeacher && selectedTeacher.images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === selectedTeacher.images.length - 1 ? 0 : prev + 1
        );
      }, 3000);
    }
  };

  const handleClosePopup = () => {
    setSelectedTeacher(null);
    setCurrentImageIndex(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const filteredTeachers = teachers.filter((teacher) =>
    filter
      ? teacher.roles.some(roleItem =>
          roleItem.toLowerCase().includes(filter.toLowerCase())
        )
      : true
  );

  return (
    <div className="teachers-list-container mt-20">
      <h1 className="text-center pt-16">Učitelé</h1>

      <div className="filters flex flex-wrap gap-2 justify-center mb-8 px-4 max-w-lg mx-auto sm:flex-nowrap sm:max-w-none">
        <button
          className={`filter-btn ${filter === "" ? "active" : ""}`}
          onClick={() => setFilter("")}
        >
          Všichni
        </button>
        <button
          className={`filter-btn ${filter === "hudebního oboru" ? "active" : ""}`}
          onClick={() => setFilter("hudebního oboru")}
        >
          Hudební obor
        </button>
        <button
          className={`filter-btn ${filter === "výtvarného oboru" ? "active" : ""}`}
          onClick={() => setFilter("výtvarného oboru")}
        >
          Výtvarný obor
        </button>
        <button
          className={`filter-btn ${filter === "tanečního oboru" ? "active" : ""}`}
          onClick={() => setFilter("tanečního oboru")}
        >
          Taneční obor
        </button>
        <button
          className={`filter-btn ${filter === "Administrativní pracovník" ? "active" : ""}`}
          onClick={() => setFilter("Administrativní pracovník")}
        >
          Administrativa
        </button>
      </div>

      <div className="teachers-list">
        {filteredTeachers.map((teacher, index) => (
          <div
            key={`${teacher.name}-${index}`}
            className={"teacher-card teacher-card:hover"}
            onClick={() => handleTeacherClick(teacher)}
          >
            <div className="teacher-image-container">
              <img
                src={teacher.profilePicture}
                alt={teacher.name}
                className="teacher-photo"
                onError={(e) => {
                  e.currentTarget.src = DEFAULT_IMAGE;
                }}
              />
            </div>
            <div className="teacher-info">
              <h2>{teacher.name}</h2>
              {teacher.subjects.length > 0 && (
                <p className="teacher-subjects">
                  <strong>Obory:</strong> {teacher.subjects.join(", ")}
                </p>
              )}
              {teacher.email && (
                <p className="teacher-email">
                  <strong>Email:</strong>{" "}
                  <a href={`mailto:${teacher.email}`}>{teacher.email}</a>
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedTeacher && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="popup-close-button"
              onClick={handleClosePopup}
              aria-label="Zavřít"
            >
              <svg 
                className="close-icon" 
                viewBox="0 0 24 24" 
                width="24" 
                height="24"
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            
            <div className="popup-content-wrapper">
              <div className="popup-left">
                <div className="teacher-images-container">
                  <div className="teacher-image-div">
                  <img
                    src={selectedTeacher.images[currentImageIndex]}
                    alt={`${selectedTeacher.name} - obrázek ${currentImageIndex + 1}`}
                    className="teacher-popup-photo"
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_IMAGE;
                    }}
                  />
                  </div>
                  
                  {selectedTeacher.images.length > 1 && (
                    <div className="image-navigation">
                      <button 
                        className="nav-button prev-button"
                        onClick={handlePrevImage}
                        aria-label="Předchozí obrázek"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </button>
                      
                      <button 
                        className="nav-button next-button"
                        onClick={handleNextImage}
                        aria-label="Další obrázek"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  {selectedTeacher.images.length > 1 && (
                    <div className="image-thumbnails">
                      {selectedTeacher.images.map((image, index) => (
                        <button
                          key={index}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => handleThumbnailClick(index)}
                        >
                          <img
                            src={image}
                            alt={`Náhled ${index + 1}`}
                            onError={(e) => {
                              e.currentTarget.src = DEFAULT_IMAGE;
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="teacher-card-center">
                  <h2>{selectedTeacher.name}</h2>
                  
                  {selectedTeacher.subjects.length > 0 && (
                    <p>
                      <strong className="text-center">Obory:</strong> {selectedTeacher.subjects.join(", ")}
                    </p>
                  )}
                  
                  {selectedTeacher.email && (
                    <div className="teacher-popup-email">
                      <strong>Email:</strong>
                      <a href={`mailto:${selectedTeacher.email}`}>{selectedTeacher.email}</a>
                    </div>
                  )}
                </div>
              </div>

              <div className="popup-right">
                {selectedTeacher.zivotopis && (
                  <div className="zivotopis-card">
                    <h3>Životopis</h3>
                    <div className="zivotopis-content">
                      {selectedTeacher.zivotopis}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeachersList;